import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../_environments/environment';
import { UrlParameters } from '../_model/shared';
import { Token } from '../_model/token';
import { Role, User } from '../_model/user';
import { OnDestroy } from '@angular/core';
import { LocalstorageService } from './LocalstorageService';
import { CouponcodeCode } from 'src/_model/couponcode';
import { defer, from } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';


export class HttpUrlEncodingCodec implements HttpParameterCodec {
  encodeKey(k: string): string { return standardEncoding(k); }
  encodeValue(v: string): string { return standardEncoding(v); }
  decodeKey(k: string): string { return decodeURIComponent(k); }
  decodeValue(v: string) { return decodeURIComponent(v); }
}
function standardEncoding(v: string): string {
  return encodeURIComponent(v);
}

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {

  private _isAuthenticatedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public user = signal<User>(null)
  public isAuthenticated$ = this._isAuthenticatedSubject.asObservable();
  public isAuthenticatedValue: boolean;

  constructor(
    private router: Router,
    private _activatedRoute: ActivatedRoute,
    private httpClient: HttpClient,
    @Inject(PLATFORM_ID) private platformId: any,
    private _localStorage: LocalstorageService,
  ) {
    if (this.isAuthenticated()) {
      this._isAuthenticatedSubject.next(true);

      if (isPlatformBrowser(this.platformId))
        this.user.set(this._localStorage.getObject('user'))
    }

    this.isAuthenticated$.subscribe(value => this.isAuthenticatedValue = value)
  }

  ngOnDestroy() {
    this._isAuthenticatedSubject.complete();
  }

  private getTimeDiff(created: number): number {
    return Math.floor(Date.now() / 1000) - created;
  }

  isAuthenticated(): boolean {
    if (isPlatformBrowser(this.platformId)) {

      const token: Token = this._localStorage.getObject('token');
      //const user: User = JSON.parse(localStorage.getItem('user')); check goes wrong in the onboarding, user is not jet fully created
      if (!token) { // || !user) {
        return false;
      }
      const tokenCreated = this.getTimeCreated();
      const timeDiff = this.getTimeDiff(tokenCreated);

      //this isnt working => it probably should get the creationdate + expires_in date and compare it to the current datetime
      if (timeDiff >= token.expires_in - 60 && timeDiff < token.expires_in) {
        // proactive near-expiry refresh; deduped across concurrent guard calls
        this.doTokenRefresh().subscribe({ error: () => { } });
      } else if (timeDiff >= token.expires_in) {
        this.doTokenRefreshForAuthenticated();
      }
      return true;
    }

    if (isPlatformServer(this.platformId)) {
      return false;
    }
  }

  /**
   * Authorization Code + PKCE, completed entirely in-page (login session -> authorize via fetch token exchange). 
   * No redirect leaves the page, so checkout/orderflow signup and the login page keep the original contract: subscribe, and the Observable completes with tokens stored.
   */
  doLogin(user): Observable<Token> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => 'Login is browser-only');
    }
    return defer(() => from(this.loginWithPkce(user.email, user.password)));
  }

  private async loginWithPkce(username: string, password: string): Promise<Token> {
    const body = JSON.stringify({ username, password, brandid: environment.brandid });
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true };
    await lastValueFrom(this.httpClient.post(environment.oauthserver + '/login/session', body, options));

    const verifier = this.randomUrlSafe(32);
    const code = await this.fetchAuthorizationCode(verifier);

    const params = new HttpParams({ encoder: new HttpUrlEncodingCodec() })
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('redirect_uri', this.redirectUri())
      .set('client_id', environment.clientid)
      .set('code_verifier', verifier);
    return lastValueFrom(this.postTokenRequest(params));
  }

  /** Runs the authorize step as an in-page fetch; the auth server hands back the code as JSON. */
  private async fetchAuthorizationCode(verifier: string): Promise<string> {
    const state = this.randomUrlSafe(16);
    const url = new URL(environment.oauthserver + '/oauth2/authorize');
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', environment.clientid);
    url.searchParams.set('redirect_uri', this.redirectUri());
    url.searchParams.set('scope', 'read write');
    url.searchParams.set('code_challenge', await this.sha256Base64Url(verifier));
    url.searchParams.set('code_challenge_method', 'S256');
    url.searchParams.set('state', state);

    // Asking for JSON makes the auth server answer 200 {code, state} instead of a 302. Following
    // the 302 cross-origin is not an option: the browser then applies CORS to our own /callback
    // response too (redirect-tainted origin), and the static host serves it without CORS headers.
    const response = await fetch(url.toString(), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body.code || body.state !== state) {
      throw new Error('Authorization failed: ' + (body.error_description ?? body.error ?? response.status));
    }
    return body.code;
  }

  // single refresh in flight; concurrent callers share its result
  private refreshInFlight$: Observable<Token> = null;

  doTokenRefresh(): Observable<Token> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => 'No token found');
    }
    if (!this.refreshInFlight$) {
      const token: Token = this._localStorage.getObject('token');
      if (!token?.refresh_token) {
        return throwError(() => 'No token found');
      }
      const params = new HttpParams({ encoder: new HttpUrlEncodingCodec() })
        .set('grant_type', 'refresh_token')
        .set('refresh_token', token.refresh_token)
        .set('client_id', environment.clientid);
      this.refreshInFlight$ = this.postTokenRequest(params).pipe(
        catchError(error => {
          // 400/401 means the auth server definitively rejected the grant (invalid_grant) ->
          // the session is dead. Anything else (offline, timeout, 502/503) is transient: the
          // non-rotating refresh token is still valid for the next attempt, so don't wipe it.
          if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 401)) {
            this.logout();
          }
          return throwError(() => error);
        }),
        finalize(() => this.refreshInFlight$ = null),
        shareReplay(1),
      );
    }
    return this.refreshInFlight$;
  }

  /** Token-endpoint POST; the response is the legacy Token shape, stored verbatim. */
  private postTokenRequest(params: HttpParams): Observable<Token> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8' }),
    };
    return this.httpClient.post<Token>(environment.oauthserver + '/oauth2/token', params, httpOptions).pipe(
      map((token: Token) => {
        if (token && token.access_token) {
          this._localStorage.setObject('token', token);
          this._localStorage.setItem('tokencreated', String(Math.floor(Date.now() / 1000)));
        }
        return token;
      }),
    );
  }

  private redirectUri(): string {
    return window.location.origin + '/callback';
  }

  private randomUrlSafe(byteLength: number): string {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    return this.base64Url(bytes);
  }

  private async sha256Base64Url(input: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
    return this.base64Url(new Uint8Array(digest));
  }

  private base64Url(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach(b => binary += String.fromCharCode(b));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /**
   * removes all the userdata from the localstorage and routes the user to the loginpage if the current url needs authentication
   */
  logout() {
    this._isAuthenticatedSubject.next(false);
    this.user.set(null);

    // best-effort auth-server session invalidation and token revocation; browser-only
    if (isPlatformBrowser(this.platformId)) {
      this.httpClient.post(environment.oauthserver + '/login/session/logout', null, { withCredentials: true })
        .subscribe({ error: () => { } });

      // revoke the refresh token server-side (SAS also invalidates the linked access token);
      // without this an exfiltrated token pair stays usable for up to 30 days after logout
      const token: Token = this._localStorage.getObject('token');
      if (token?.refresh_token) {
        const revokeParams = new HttpParams({ encoder: new HttpUrlEncodingCodec() })
          .set('token', token.refresh_token)
          .set('token_type_hint', 'refresh_token')
          .set('client_id', environment.clientid);
        this.httpClient.post(environment.oauthserver + '/oauth2/revoke', revokeParams, {
          headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8' }),
        }).subscribe({ error: () => { } });
      }
    }

    this._localStorage.removeItem('token');
    this._localStorage.removeItem('tokencreated');
    this._localStorage.removeItem('user');
    this._localStorage.removeItem('pricecountry');
    this._localStorage.removeItem('preferredlanguage');
    this._localStorage.removeItem('paymentReturnUrl');

    // when customers return it shouldnt remove couponcode, only if contains personal data
    const couponcode = this._localStorage.getObject('couponcode') as CouponcodeCode
    if (couponcode && couponcode.personalemail)
      this._localStorage.removeItem('couponcode');

    //should return false if it is a route for which authentcation is needed
    if (this._activatedRoute.routeConfig && this._activatedRoute?.routeConfig?.canActivate.length > 0) {
      const reg = RegExp(/returnUrl=.*/gm)
      const url = this.router.url.toString().replace(reg, "");
      this.router.navigate(['login'], { queryParams: { returnUrl: url } });
    }
  }

  private async doTokenRefreshForAuthenticated() {
    // logout on a definitive rejection happens inside doTokenRefresh; transient failures
    // are swallowed here so a network blip does not destroy a still-valid session
    return await lastValueFrom(this.doTokenRefresh()).catch(() => { });
  }

  getUserDetails(): Observable<User> {
    return this.httpClient.get<User>(environment.oauthserver + '/user/v1/user/details').pipe(
      map((user: User) => {

        //is not a customer user and not telemarketing user -> should not login to website
        if (user && !user.customerid && !user.userroles.includes(Role.TELEMARKETING)) {
          this.logout();
          this.router.navigate(['login']);
          return null;
        }

        if (user && user.userroles.includes(Role.TELEMARKETING)) 
          this.router.navigate(['telemarketing']);

        this.user.set(user);
        this._localStorage.setObject('user', user);
        this._isAuthenticatedSubject.next(true);
        return user;

      })
    );
  }

  /**
   * 
   * @deprecated Is replaced by the user signal
   */
  getCurrentUser(): User {

    if (isPlatformServer(this.platformId))
      return null;

    let user = this._localStorage.getObject('user') as User;
    return user;

  }
  patchUser(userForm: FormGroup): Observable<any> {
    const user = userForm.getRawValue();
    user.lastmodified = Date.now();

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };

    if (userForm.controls['role'].dirty) {
      const body = JSON.stringify(user.role);
      return this.httpClient.patch(environment.oauthserver + '/user/v1/user/' + user.id + '/role', body, options).pipe(
        map((response: string) => <string>response)
        , catchError(this.handleError)
      );
    }
  }
  createUser(user: User, password: string = null): Observable<User> {
    const params = new UrlParameters();

    if (!user.username)
      return throwError('No username!')

    if (password)
      params.addParameter('password', password)

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };

    const body = JSON.stringify(user);

    return this.httpClient.post(environment.oauthserver + '/user/v1/user/add' + params.toString(), body, options).pipe(
      map((response: User) => <User>response)
      , catchError(this.handleError)
    );
  }
  updateUser(user: User, password: string = null): Observable<User> {
    const params = new UrlParameters();

    params.addParameter('password', password)
    if (password)
      params.addParameter('encryptPassword', 'true')

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };

    const body = JSON.stringify(user);

    return this.httpClient.put(environment.oauthserver + '/user/v1/user/' + params.toString(), body, options).pipe(
      map((response: User) => <User>response)
      , catchError(this.handleError)
    );
  }
  addCustomerid(activationkey: string, customerid: number): Observable<string> {

    const customeridactivation = {
      activationKey: activationkey,
      customerId: customerid
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };

    const body = JSON.stringify(customeridactivation);

    return this.httpClient.patch(environment.oauthserver + '/user/v1/user/add/customerid', body, options).pipe(
      map((response: string) => <string>response)
      , catchError(this.handleError)
    );

  }
  isEmailValid(email: string): Observable<boolean> {

    if (!email || email === '') {
      return of(false);
    }

    const params = new UrlParameters();
    params.addParameter('email', email);

    return this.httpClient.get(environment.apiserver + 'auth/user/v1/user/emailvalid' + params.toString()).pipe(
      map((result: boolean) => { return result })
      , catchError(this.handleError)
    );
  }

  changepassword(oldpassword: string, newpassword: string): Observable<any> {
    const urlparams = new UrlParameters();
    urlparams.addParameter('oldpassword', oldpassword)
    urlparams.addParameter('newpassword', newpassword)

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };
    const body = '';

    return this.httpClient.post(environment.oauthserver + '/user/v1/user/changepassword/loggedin' + urlparams.toString(), body, options).pipe(
      map((response: any) => <any>response)
      , catchError(this.handleError)
    );

  }
  changepasswordlink(username: string): Observable<any> {

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { headers: headers };

    const obj = {
      username: username,
      brandid: environment.brandid
    }

    const body = JSON.stringify(obj);

    return this.httpClient.post(environment.oauthserver + '/user/v1/user/changepassword/request', body, options).pipe(
      map((response: any) => <any>response)
      , catchError(this.handleError)
    );

  }
  changepasswordkey(newpassword: string, key: string): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    //const options = { headers: headers, observe: 'response', responseType: 'text' };
    const obj = {
      password: newpassword,
      resetpasswordkey: key
    }
    const body = JSON.stringify(obj);

    return this.httpClient.post(environment.oauthserver + '/user/v1/user/changepassword', body, { headers: headers, observe: 'response', responseType: 'text' }).pipe(
      map((response: any) => {
        return response.body
      })
      , catchError(this.handleError)
    );

  }

  hasRole(role: Role): boolean {
    let currentuser = this.getCurrentUser();
    if (currentuser)
      return currentuser.userroles.includes(role);

    return false;
  }


  private getTimeCreated(): number {
    if (isPlatformBrowser(this.platformId)) {
      let tokenCreated: number = parseInt(this._localStorage.getItem('tokencreated'), 10);
      // we have a token try and refresh; the time will be set then
      if (!tokenCreated)
        this.doTokenRefreshForAuthenticated();

      return tokenCreated
      // tokenCreated = parseInt(this._localStorage.getItem('tokencreated'), 10);
      // return tokenCreated;
    }
    return 0
  }

  private handleError(error: Response) {
    if (error.status === 400) {
      return throwError(() => 'Bad request');
    }
    return throwError(() => error || 'Server error');
  }
}
